import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { apiPost } from "@/lib/api/client";
import { toast } from "@/components/ui/toast";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface RebuildResult {
  rows_updated: number;
  drift_detected: number;
}

export function InventoryRebuildPage() {
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState<RebuildResult | null>(null);

  const rebuild = useMutation({
    mutationFn: () => apiPost<RebuildResult>("/v1/admin/inventory/rebuild", { confirm }),
    onSuccess: (data) => {
      setResult(data);
      setConfirm("");
      toast.success(
        `Đã rebuild: ${data.rows_updated} dòng, phát hiện ${data.drift_detected} lệch`,
      );
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  return (
    <div className="max-w-lg space-y-6">
      <h1 className="text-xl font-semibold">Rebuild Tồn Kho</h1>

      {/* Warning banner */}
      <div className="flex gap-3 rounded-lg border border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-600 dark:bg-yellow-900/20">
        <AlertTriangle size={20} className="mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400" />
        <div className="text-sm space-y-1">
          <p className="font-medium text-yellow-800 dark:text-yellow-300">Cảnh báo: thao tác này khoá bảng</p>
          <p className="text-yellow-700 dark:text-yellow-400">
            Rebuild tính toán lại toàn bộ tồn kho từ nhật ký giao dịch và khoá bảng
            <code className="mx-1 rounded bg-yellow-100 px-1 dark:bg-yellow-900">stock_levels</code>
            trong suốt quá trình. Nên chạy ngoài giờ cao điểm.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          Nhập <code className="rounded bg-muted px-1 font-mono text-sm">REBUILD</code> để xác nhận
        </Label>
        <Input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="REBUILD"
          className="font-mono"
        />
      </div>

      <Button
        variant="destructive"
        disabled={confirm !== "REBUILD" || rebuild.isPending}
        onClick={() => rebuild.mutate()}
      >
        {rebuild.isPending ? "Đang rebuild…" : "Bắt đầu Rebuild"}
      </Button>

      {result && (
        <div className="rounded-lg border bg-card p-4 space-y-2 text-sm">
          <p className="font-medium">Kết quả</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded bg-muted p-3 text-center">
              <p className="text-2xl font-bold">{result.rows_updated}</p>
              <p className="text-xs text-muted-foreground">Dòng đã cập nhật</p>
            </div>
            <div className={`rounded p-3 text-center ${result.drift_detected > 0 ? "bg-yellow-50 dark:bg-yellow-900/30" : "bg-muted"}`}>
              <p className="text-2xl font-bold">{result.drift_detected}</p>
              <p className="text-xs text-muted-foreground">Lệch phát hiện</p>
            </div>
          </div>
          {result.drift_detected === 0 && (
            <p className="text-xs text-green-700 dark:text-green-400">
              Không có lệch — dữ liệu tồn kho đã nhất quán.
            </p>
          )}
          {result.drift_detected > 0 && (
            <p className="text-xs text-yellow-700 dark:text-yellow-400">
              Đã sửa {result.drift_detected} dòng lệch. Kiểm tra nhật ký giao dịch nếu nghi ngờ.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
